import { useEffect, useState } from "react";
import { getUtm, track } from "../lib/tracking";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mic2, Briefcase, Calendar, Newspaper, Heart } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { COUNTRIES } from "../lib/countries";

// 5 account types. `client` is the default "fan" account. Visitors can also
// browse without an account at all.
const ROLES = [
    { v: "client", l: "Fan", icon: Heart, accent: "#00A86B", desc: "Écouter, soutenir, offrir des cadeaux à mes artistes." },
    { v: "artist", l: "Artiste", icon: Mic2, accent: "#D4AF37", desc: "Chanteur, rappeur, DJ, danseur, humoriste, instrumentiste…" },
    { v: "provider", l: "Prestataire", icon: Briefcase, accent: "#FF8C42", desc: "Beatmaker, producteur, photographe, vidéaste, ingé son…" },
    { v: "organizer", l: "Organisateur", icon: Calendar, accent: "#8b3dff", desc: "Festival, promoteur, salle, hôtel, bar, municipalité…" },
    { v: "media", l: "Média", icon: Newspaper, accent: "#3DA5FF", desc: "Journaliste, radio, TV, blog, influenceur…" },
];

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const initialRole = ROLES.find((r) => r.v === params.get("role"))?.v || "client";
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [role, setRole] = useState(initialRole);
    const [country, setCountry] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const ref = params.get("ref");
        if (ref) {
            localStorage.setItem("mboa_ref", ref);
            api.post("/affiliates/track-visit", { ref }).catch(() => {});
        }
    }, []); // eslint-disable-line

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        const res = await register({ name, email, password, role, country, ref: params.get("ref") || localStorage.getItem("mboa_ref") || undefined, utm: getUtm() || undefined });
        setLoading(false);
        if (res.ok) {
            track("CompleteRegistration", { content_name: role, status: true });
            // Redirect to a dedicated "check your inbox" screen — the user must
            // verify their email before being able to log in.
            nav(`/check-email?email=${encodeURIComponent(email)}`, { replace: true });
        } else {
            setError(res.error);
        }
    };

    const selected = ROLES.find((r) => r.v === role) || ROLES[0];

    return (
        <div className="min-h-[80vh] flex items-center justify-center px-6 py-12" data-testid="register-page">
            <div className="w-full max-w-3xl bg-[#161616] border border-[#262626] rounded-2xl p-8 md:p-12">
                <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-center">
                    Crée ton compte MBOAZICK
                </h1>
                <p className="mt-2 text-sm text-[#A8A8A8] text-center">La maison numérique de l'écosystème musical africain.</p>

                <p className="mt-8 text-xs uppercase tracking-widest text-[#6E6E6E] text-center">Tu es ?</p>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3" role="tablist">
                    {ROLES.map((r) => {
                        const active = r.v === role;
                        const Icon = r.icon;
                        return (
                            <button
                                key={r.v}
                                type="button"
                                onClick={() => setRole(r.v)}
                                className="relative rounded-xl border bg-[#0F0F0F] px-3 py-4 transition-all hover:-translate-y-0.5"
                                style={{ borderColor: active ? r.accent : "#262626", boxShadow: active ? `0 0 0 1px ${r.accent}` : "none" }}
                                data-testid={`role-${r.v}`}
                            >
                                <div className="w-9 h-9 mx-auto rounded-full flex items-center justify-center" style={{ background: `${r.accent}1A`, color: r.accent }}>
                                    <Icon className="w-4 h-4" />
                                </div>
                                <p className="mt-2 text-sm font-medium" style={{ color: active ? r.accent : "#F4F4F4" }}>{r.l}</p>
                            </button>
                        );
                    })}
                </div>
                <p className="mt-3 text-xs text-[#A8A8A8] text-center min-h-[1.5em]" data-testid="role-desc">{selected.desc}</p>

                <form onSubmit={submit} className="mt-8 space-y-4 max-w-md mx-auto">
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#6E6E6E]">Nom / Alias</label>
                        <input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 w-full bg-[#0F0F0F] border border-[#262626] focus:border-[#00A86B] rounded-lg px-4 py-3 focus:outline-none" data-testid="register-name-input" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#6E6E6E]">Email</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 w-full bg-[#0F0F0F] border border-[#262626] focus:border-[#00A86B] rounded-lg px-4 py-3 focus:outline-none" data-testid="register-email-input" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#6E6E6E]">Pays</label>
                        <select value={country} onChange={(e) => setCountry(e.target.value)} required className="mt-1 w-full bg-[#0F0F0F] border border-[#262626] focus:border-[#00A86B] rounded-lg px-4 py-3 focus:outline-none text-[#F4F4F4]" data-testid="register-country-select">
                            <option value="" disabled>Choisis ton pays…</option>
                            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#6E6E6E]">Mot de passe</label>
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1 w-full bg-[#0F0F0F] border border-[#262626] focus:border-[#00A86B] rounded-lg px-4 py-3 focus:outline-none" data-testid="register-password-input" />
                    </div>

                    {(role === "artist" || role === "provider" || role === "organizer" || role === "media") && (
                        <p className="text-xs text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-3" data-testid="pro-pending-note">
                            Ton profil pro sera vérifié sous 24-48h. En attendant, tu peux compléter tes infos et explorer la plateforme.
                        </p>
                    )}

                    {error && <p className="text-sm text-[#E53E3E]" data-testid="register-error">{error}</p>}

                    <button type="submit" disabled={loading} className="w-full font-semibold px-6 py-3 rounded-full transition-colors text-[#0F0F0F] hover:opacity-90 disabled:opacity-60" style={{ background: selected.accent }} data-testid="register-submit-button">
                        {loading ? "Création…" : `Créer mon compte ${selected.l.toLowerCase()}`}
                    </button>
                </form>

                <p className="mt-6 text-sm text-[#A8A8A8] text-center">
                    Déjà inscrit ? <Link to="/login" className="text-[#00A86B] hover:underline">Se connecter</Link>
                </p>
            </div>
        </div>
    );
}
